import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { signInGoogle, signInApple } from "../lib/auth";
import { Icon, Logo } from "../components/ui";

export const Route = createFileRoute("/login")({ component: LoginPage });

function LoginPage() {
  const { t } = useTranslation();
  return (
    <main className="min-h-[calc(100vh-56px)] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-10">
          <div className="inline-flex mb-7 text-ink">
            <Logo size="lg" />
          </div>
          <h1 className="font-serif text-[32px] leading-[1.15] font-medium tracking-tight text-ink mb-3">
            {t("login.title")} <span className="italic text-ink-dim">{t("login.titleEm")}</span>
          </h1>
          <p className="text-[14.5px] text-ink-dim leading-relaxed">{t("login.subtitle")}</p>
        </div>

        <div className="space-y-2.5">
          <button
            onClick={() => signInGoogle()}
            className="w-full h-12 px-4 rounded-lg border border-border-strong bg-surface text-ink text-[14.5px] font-medium hover:bg-surface-2 transition-colors flex items-center gap-3 ring-focus"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.07 5.07 0 0 1-2.2 3.32v2.76h3.55c2.08-1.92 3.29-4.74 3.29-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.55-2.76c-.98.66-2.24 1.05-3.73 1.05-2.87 0-5.3-1.94-6.16-4.54H2.18v2.85A11 11 0 0 0 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.66 2.84C6.7 7.31 9.13 5.38 12 5.38z"
              />
            </svg>
            {t("login.google")}
          </button>
          <button
            onClick={() => signInApple()}
            className="w-full h-12 px-4 rounded-lg bg-ink text-bg text-[14.5px] font-medium hover:bg-ink/90 transition-colors flex items-center gap-3 ring-focus"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M16.37 1.43c0 1.14-.42 2.15-1.26 3.04-1 1.06-2.2 1.67-3.5 1.56-.02-1.12.4-2.23 1.25-3.16.96-1.06 2.27-1.65 3.5-1.44zM20.5 17.42c-.52 1.18-1.13 2.27-1.84 3.27-.97 1.37-1.77 2.32-2.38 2.85-.95.86-1.96 1.3-3.05 1.32-.78.02-1.72-.21-2.81-.7-1.1-.49-2.1-.72-3.02-.72-.96 0-1.99.23-3.1.72-1.1.49-2 .74-2.68.77-1.04.05-2.08-.4-3.1-1.35-.65-.6-1.49-1.59-2.5-2.95C-1.46 18-.36 15.85.08 14.04.43 12.55 1.31 11.34 2.7 10.4c.79-.53 1.74-.82 2.85-.84.81 0 1.88.27 3.21.78 1.33.51 2.18.78 2.55.78.28 0 1.24-.31 2.86-.92 1.54-.57 2.83-.8 3.9-.71 2.89.24 5.05 1.43 6.5 3.59-2.58 1.6-3.86 3.83-3.83 6.7.02 2.22.8 4.06 2.34 5.51-.7 1-1.46 1.93-2.27 2.79z" />
            </svg>
            {t("login.apple")}
          </button>
        </div>

        <div className="my-7 flex items-center gap-3">
          <div className="flex-1 divider-dashed" />
          <div className="text-[11px] text-ink-faint uppercase tracking-wider font-medium">
            {t("login.visibilityEyebrow")}
          </div>
          <div className="flex-1 divider-dashed" />
        </div>

        <div className="rounded-lg border border-border bg-surface-2 p-4 text-[13px] text-ink-dim leading-relaxed">
          <div className="flex items-start gap-3">
            <Icon name="globe" size={16} className="text-ink-faint mt-0.5 shrink-0" />
            <div>{t("login.visibilityNote")}</div>
          </div>
        </div>

        <div className="mt-8 text-center text-[12.5px] text-ink-faint">
          {t("login.agree")}{" "}
          <a
            href="#"
            className="text-ink-dim hover:text-ink underline underline-offset-2 decoration-border-strong"
          >
            {t("login.terms")}
          </a>{" "}
          {t("login.and")}{" "}
          <a
            href="#"
            className="text-ink-dim hover:text-ink underline underline-offset-2 decoration-border-strong"
          >
            {t("login.privacy")}
          </a>
          .
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/"
            className="text-[12.5px] text-ink-faint hover:text-ink inline-flex items-center gap-1.5"
          >
            <Icon name="arrow-left" size={13} /> {t("login.back")}
          </Link>
        </div>
      </div>
    </main>
  );
}
